import { config } from "../config";
import { fetchWithAuthRetry } from "../utils/fetchWithAuthRetry";
import { TSession } from "../utils/types";

const protocol = config.coreApi.protocol;
const host = config.coreApi.host;
const urlPrefixSettings = `${protocol}://${host}/clinics/v1/settings`;

export type TCustomEncounterType = {
  id: number;
  clinic_uuid: string;
  name: string;
  created_at: string;
  updated_at: string;
};

const resolveClinicId = (
  session: TSession | null,
  clinicId?: string | null,
) => {
  const resolved = clinicId ?? session?.clinicId ?? null;

  if (!resolved) {
    throw new Error("Clinic ID is required to manage encounter types.");
  }

  return resolved;
};

export const listCustomEncounterTypes = async (
  session: TSession | null,
  clinicId?: string | null,
): Promise<TCustomEncounterType[]> => {
  const resolvedClinicId = resolveClinicId(session, clinicId);
  const url = `${urlPrefixSettings}/${resolvedClinicId}/custom-encounter-types`;

  const response = await fetchWithAuthRetry(url, {
    method: "GET",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch custom encounter types: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};

export const createCustomEncounterType = async (
  session: TSession | null,
  name: string,
  clinicId?: string | null,
): Promise<TCustomEncounterType> => {
  const resolvedClinicId = resolveClinicId(session, clinicId);
  const url = `${urlPrefixSettings}/${resolvedClinicId}/custom-encounter-types`;

  const response = await fetchWithAuthRetry(url, {
    method: "POST",
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to create custom encounter type: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};

export type TClinicDetails = {
  id: number;
  clinic_uuid: string;
  note_integration_sync_timing: "on_note_creation" | "on_signature";
  timezone: string | null;
  created_at: string;
  updated_at: string;
};

export type TUpdateClinicDetailsRequest = {
  note_integration_sync_timing?: "on_note_creation" | "on_signature";
  timezone?: string | null;
};

export const getClinicDetails = async (
  session: TSession | null,
  clinicId?: string | null,
): Promise<TClinicDetails> => {
  const resolvedClinicId = resolveClinicId(session, clinicId);
  const url = `${urlPrefixSettings}/${resolvedClinicId}/details`;

  const response = await fetchWithAuthRetry(url, {
    method: "GET",
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to fetch clinic details: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};

export const updateClinicDetails = async (
  session: TSession | null,
  updates: TUpdateClinicDetailsRequest,
  clinicId?: string | null,
): Promise<TClinicDetails> => {
  const resolvedClinicId = resolveClinicId(session, clinicId);
  const url = `${urlPrefixSettings}/${resolvedClinicId}/details`;

  const response = await fetchWithAuthRetry(url, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to update clinic details: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};
